import React from 'react';
import { SectorForecastResult, SectorForecastPoint } from './sectorForecastUtils';

/**
 * Compact 10-cell visualisation of a sector's publisher-weighted forward
 * forecast — one column per timeframe (1h, 1d, 1w, 2w, 4w, 2m, 3m, 4m, 5m, 6m).
 * Mirrors the role that {@code PriceSnapshotMiniChart} plays on the per-stock
 * article cards, but at the SECTOR level: cells are coloured green if the
 * conviction-weighted expected return at that horizon is positive, red if
 * negative, opacity scales with |%change| capped at ±10%.
 *
 * Each cell shows:
 *   - the timeframe label (1h, 1d, ...) along the bottom
 *   - the signed expected % return centred
 *   - a thin vertical whisker spanning ±1σ
 *
 * Hover any cell for the full breakdown (expected, ±1σ band, contributing
 * (article × ticker) pair count) via the native `title` attribute.
 *
 * Empty-state rendering: when the publisher-metrics backfill hasn't produced
 * any usable scored publisher for sector articles yet (or every article has
 * stance==0), the strip renders an "awaiting publisher backfill" caption so
 * the user knows the forecast is pending rather than zero.
 */
export interface SectorForecastStripProps {
    forecast: SectorForecastResult;
}

const HEIGHT = 90;
const PCT_CAP = 10;

function colorForPct(pct: number): { bg: string; fg: string } {
    const mag = Math.min(Math.abs(pct) / PCT_CAP, 1);
    // Floor opacity at 0.18 so even tiny moves are perceptible against white.
    const opacity = 0.18 + 0.62 * mag;
    if (pct >= 0) {
        return { bg: `rgba(34,197,94,${opacity.toFixed(2)})`, fg: '#0f4d23' };
    }
    return { bg: `rgba(239,68,68,${opacity.toFixed(2)})`, fg: '#5a1212' };
}

function formatPct(pct: number): string {
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(pct >= 10 || pct <= -10 ? 1 : 2)}%`;
}

const ForecastCell: React.FC<{ point: SectorForecastPoint }> = ({ point }) => {
    const { expectedPct, sigmaPct, upperPct, lowerPct, contributingArticles, label } = point;
    const { bg, fg } = colorForPct(expectedPct);
    const title =
        `${label}: ${formatPct(expectedPct)} ` +
        `(band ${formatPct(lowerPct)} … ${formatPct(upperPct)}, σ=${sigmaPct.toFixed(2)}%) ` +
        `· ${contributingArticles} contribution${contributingArticles === 1 ? '' : 's'}`;

    return (
        <div
            title={title}
            style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 4,
                padding: '8px 4px 6px',
                background: bg,
                borderRadius: 6,
                color: fg,
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.1,
            }}
        >
            <span style={{ fontWeight: 700 }}>{formatPct(expectedPct)}</span>
            {/* ±1σ whisker — a thin vertical bar that visualises uncertainty. */}
            <span
                style={{
                    width: 2,
                    height: 18,
                    background: fg,
                    opacity: 0.4,
                    borderRadius: 1,
                    flexShrink: 0,
                }}
            />
            <span style={{ fontSize: 10, opacity: 0.75, letterSpacing: 0.4 }}>
                {label}
            </span>
        </div>
    );
};

export const SectorForecastStrip: React.FC<SectorForecastStripProps> = ({ forecast }) => {
    if (!forecast.hasSignal) {
        return (
            <div
                style={{
                    height: HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                    fontSize: 13,
                    background: '#f9fafb',
                    borderRadius: 6,
                    border: '1px dashed #e5e7eb',
                }}
            >
                Forecast awaiting publisher backfill {forecast.reason ? `· ${forecast.reason}` : ''}
            </div>
        );
    }
    return (
        <div style={{ display: 'flex', gap: 4, height: HEIGHT }}>
            {forecast.points.map(p => (
                <ForecastCell key={p.label} point={p} />
            ))}
        </div>
    );
};
